/**
 * VAGUE 33A — Post-claim onboarding widget.
 *
 * Guides the merchant through 3 quick wins right after they claim their
 * fiche. Auto-hides once all 3 are done (with a celebratory toast) or if
 * they explicitly dismiss it.
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Sparkle, Camera, PencilLine, EnvelopeSimple, CheckCircle, X, ArrowRight, Plus, Trash,
} from "@phosphor-icons/react";

const stepMeta = {
  photo:   { label: "Ajoute une photo",       icon: <Camera size={16} weight="duotone"/>, cta: "Uploader une photo",  target: "#photos-section" },
  pitch:   { label: "Écris ton pitch (≥100 caractères)", icon: <PencilLine size={16} weight="duotone"/>, cta: "Écrire mon pitch", target: "#profile-section" },
  invites: { label: "Invite 3 clients à laisser un avis", icon: <EnvelopeSimple size={16} weight="duotone"/>, cta: "Inviter 3 clients", target: "invites" },
};

function StepRow({ id, done, label, icon, onCta }) {
  return (
    <div
      className={`flex items-center gap-3 p-3 border rounded-sm transition-colors ${done ? "bg-emerald-50 border-emerald-200" : "bg-card border-border"}`}
      data-testid={`onboarding-step-${id}`}
    >
      <div className={`shrink-0 w-8 h-8 rounded-full grid place-items-center ${done ? "bg-emerald-500 text-white" : "bg-primary/10 text-primary"}`}>
        {done ? <CheckCircle size={18} weight="fill"/> : icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-medium text-sm ${done ? "text-emerald-900 line-through" : ""}`}>{label}</div>
      </div>
      {!done && (
        <button
          onClick={onCta}
          className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1 shrink-0"
          data-testid={`onboarding-step-${id}-cta`}
        >
          {stepMeta[id].cta} <ArrowRight size={12} weight="bold"/>
        </button>
      )}
    </div>
  );
}

export default function PostClaimOnboarding({ slug, onCompleted }) {
  const [state, setState] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [emails, setEmails] = useState(["", "", ""]);
  const [showInvitesForm, setShowInvitesForm] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api.get(`/dashboard/${slug}/onboarding`)
      .then((r) => setState(r.data))
      .catch(() => {});
  }, [slug]);

  if (!state) return null;
  if (state.dismissed) return null;
  if (state.done) {
    // Auto-dismiss once fully completed
    return (
      <div className="mb-4 flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-sm p-3" data-testid="post-claim-onboarding-done">
        <CheckCircle size={22} weight="fill" className="text-emerald-500 shrink-0"/>
        <div className="flex-1">
          <div className="font-bold text-sm">🎉 Vitrine complète — 3/3</div>
          <div className="text-xs">Bravo ! Ta fiche est prête à convertir tes visiteurs. Les avis clients arriveront progressivement.</div>
        </div>
        <button
          onClick={async () => {
            await api.post(`/dashboard/${slug}/onboarding/dismiss`).catch(() => {});
            setState({ ...state, dismissed: true });
          }}
          className="text-xs underline decoration-2 underline-offset-2 hover:opacity-80"
          data-testid="post-claim-onboarding-hide"
        >Masquer</button>
      </div>
    );
  }

  const handleCta = (id) => {
    if (id === "invites") { setShowInvitesForm(true); return; }
    // Scroll into a section — best effort
    const target = stepMeta[id].target;
    if (target?.startsWith("#")) {
      const el = document.querySelector(target);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const sendInvites = async () => {
    const clean = emails.map(e => (e || "").trim()).filter(Boolean);
    if (clean.length < 1) { toast.error("Renseigne au moins un email"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/dashboard/${slug}/review-invites`, { emails: clean });
      toast.success(`✅ ${r.data.sent} invitation${r.data.sent > 1 ? "s" : ""} envoyée${r.data.sent > 1 ? "s" : ""}`);
      // Refresh onboarding state
      const fresh = await api.get(`/dashboard/${slug}/onboarding`);
      setState(fresh.data);
      setEmails(["", "", ""]);
      setShowInvitesForm(false);
      if (fresh.data.done) {
        toast.success("🎉 Vitrine complète — 3/3 !", { duration: 5000 });
        onCompleted?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-6 border border-primary/30 bg-primary/5 rounded-sm p-4" data-testid="post-claim-onboarding">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Sparkle size={18} weight="duotone" className="text-primary"/>
          <h2 className="font-display text-base font-medium">Complète ta vitrine en 3 étapes</h2>
        </div>
        <div className="text-xs font-mono bg-primary text-primary-foreground px-2 py-0.5 rounded" data-testid="onboarding-progress">
          {state.completed_count}/{state.total_steps}
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
        Les 3 vitrines les plus complètes du Haut-Rhin reçoivent <b>2× plus de clics</b>. En 5 min, tu passes devant.
      </p>

      <div className="space-y-2">
        <StepRow id="photo"   done={state.steps.photo}   label={stepMeta.photo.label}   icon={stepMeta.photo.icon}   onCta={() => handleCta("photo")}/>
        <StepRow id="pitch"   done={state.steps.pitch}   label={stepMeta.pitch.label}   icon={stepMeta.pitch.icon}   onCta={() => handleCta("pitch")}/>
        <StepRow id="invites" done={state.steps.invites} label={`Invite 3 clients à laisser un avis (${state.invites_sent_count}/${state.invites_target})`} icon={stepMeta.invites.icon} onCta={() => handleCta("invites")}/>
      </div>

      {showInvitesForm && (
        <div className="mt-4 pt-4 border-t border-primary/20 space-y-2" data-testid="onboarding-invites-form">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Emails de tes clients (min 1, max 20 par lot)
          </div>
          {emails.map((e, i) => (
            <div key={i} className="flex gap-2">
              <input
                type="email"
                value={e}
                onChange={(ev) => setEmails(prev => prev.map((v, j) => j === i ? ev.target.value : v))}
                placeholder={`client${i + 1}@exemple.com`}
                className="flex-1 border border-border rounded-sm px-3 py-2 text-sm bg-background outline-none focus:border-primary"
                data-testid={`onboarding-invite-email-${i}`}
              />
              {emails.length > 1 && (
                <button
                  onClick={() => setEmails(prev => prev.filter((_, j) => j !== i))}
                  className="text-muted-foreground hover:text-destructive p-2"
                  aria-label="Retirer"
                  data-testid={`onboarding-invite-remove-${i}`}
                ><Trash size={14}/></button>
              )}
            </div>
          ))}
          <div className="flex items-center justify-between gap-2 pt-1">
            <button
              type="button"
              onClick={() => setEmails(prev => prev.length < 20 ? [...prev, ""] : prev)}
              className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1"
              data-testid="onboarding-invite-add"
            >
              <Plus size={12} weight="bold"/> Ajouter un email
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setShowInvitesForm(false); setEmails(["", "", ""]); }}
                className="text-xs px-3 py-1.5 text-muted-foreground hover:text-foreground"
                data-testid="onboarding-invites-cancel"
              >Annuler</button>
              <button
                onClick={sendInvites}
                disabled={busy || emails.every(e => !(e || "").trim())}
                className="text-xs px-3 py-1.5 bg-primary text-primary-foreground rounded-sm font-semibold hover:bg-[#0f3d29] disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="onboarding-invites-submit"
              >
                {busy ? "Envoi…" : <>Envoyer <ArrowRight size={12} weight="bold"/></>}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-primary/10 flex items-center justify-end">
        <button
          onClick={async () => {
            await api.post(`/dashboard/${slug}/onboarding/dismiss`).catch(() => {});
            setState({ ...state, dismissed: true });
            toast.info("Guide masqué. Tu peux toujours compléter ta vitrine plus bas.");
          }}
          className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          data-testid="post-claim-onboarding-dismiss"
        >
          <X size={11}/> Masquer ce guide
        </button>
      </div>
    </div>
  );
}
